// AuthCuadroMandoProvider.js
import { useState, useEffect, createContext, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { useDispatch } from 'react-redux';

import { setPermissionsSlice } from "./../../../Redux/permissions/permissionDataSlice";
import { setUserDataSlice } from "./../../../Redux/permissions/UserDataSlice";
import { clearPersistedState } from "../../../Redux/app/store";

import { WarningOutlined } from "@ant-design/icons";
import LoginService from "../../../services/LoginService";

import UserService from "../../../services/UserService";
import { LogoutMSSO } from "../../../services/LoginMSSOService";

import { GET_TOKEN_SYSTEM, CATALOGO_MENSAJES, COLOR_WARNING, REMOVE_TOKEN_SYSTEM } from "../../../shared/constants";

import CustomModal from "../../../component/ModalCustom";
import alerts from "../../../shared/alerts";


const AuthCuadroMandoContext = createContext({
    isAuthenticated: null,
    loading: false,
    logOut: () => { },
    getUserCMIProfile: () => { },
    AvailableRoutes: [],
});

export const PrivateCuadroMandoProvider = ({ children }) => {
    const { instance, accounts } = useMsal();

    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [isAuthenticated, setIsAuthenticated] = useState(null);
    const [loading, setLoading] = useState(false);
    const [AvailableRoutes, setAvailableRoutes] = useState([]);
    const [isLoggingIn, setIsLoggingIn] = useState(false);

    const [dataModal, setDataModal] = useState(
        {
            icon: <WarningOutlined />,
            title: "",
            message: CATALOGO_MENSAJES.messageLogOut,
            modalVisible: false,
            color: COLOR_WARNING,
            success: false
        }
    );

    const isEgresadosRoute = location.pathname.startsWith('/egresados');

    useEffect(() => {
        const initializeAuth = async () => {
            setLoading(true);

            if (isEgresadosRoute) {
                setIsAuthenticated(false);
                setLoading(false);
                return;
            }

            const token = GET_TOKEN_SYSTEM();
            if (!token) {
                setIsAuthenticated(false);
                setLoading(false);
                return;
            }

            // Verificar token existente
            const response = await LoginService.checkToken(token);
            if (response.success) {
                await getUserCMIProfile();
            } else {
                setIsAuthenticated(false);
                await logOutTokenExpired();
            }
            setLoading(false);
        };

        setAvailableRoutes([]);
        initializeAuth();
    }, []);

    useEffect(() => {
        if (isEgresadosRoute || isLoggingIn) return;
        const handleAccountsChange = async () => {
            if (accounts?.length > 0 && isAuthenticated === false) return;

            if (accounts?.length === 0 && isAuthenticated === true) {
                setIsAuthenticated(false);
                await logOutTokenExpired();
            }
        };
        handleAccountsChange();
    }, [accounts]);

    const getUserCMIProfile = async () => {
        setLoading(true);
        setIsLoggingIn(true);
        const response_user_profile = await UserService.getUserProfile();
        if (!response_user_profile.success) {
            setIsAuthenticated(false);
            await logOutTokenExpired();
        }
        setLoading(false);

        if (response_user_profile.success) {
            dispatch(
                setUserDataSlice({
                    user: `${response_user_profile.data?.nombre} ${response_user_profile.data?.primer_apellido} ${response_user_profile.data?.segundo_apellido}`,
                    correo_institucional: response_user_profile?.data?.correo_institucional,
                    rol: response_user_profile?.data?.usuario_rol,
                    alcance: response_user_profile.data?.alcance?.descripcion_alcance,
                    adscripcion:
                    {
                        cmi_areas: response_user_profile.data?.cmi_areas,
                        plantel: response_user_profile.data?.plantel
                    }
                })
            )

            dispatch(
                setPermissionsSlice({
                    correo_institucional: response_user_profile?.data?.correo_institucional,
                    menu: response_user_profile?.data?.menus,
                    permissions: response_user_profile?.data?.permisos,
                    routers: response_user_profile?.data?.routers,
                })
            );

            setAvailableRoutes(response_user_profile?.data?.routers ?? []); // Actualiza las rutas disponibles
            setIsAuthenticated(true); // Actualiza el estado de autenticación 
        }
        setIsLoggingIn(false);
    };

    const showModal = (message) => {
        setDataModal(
            {
                ...dataModal,
                message: CATALOGO_MENSAJES[message],
                modalVisible: true
            }
        );
    }
    const logOut = async () => {
        showModal('LOG_AUTH');
    };

    const logOutTokenExpired = async () => {
        showModal('TOKEN_EXPIRED');
    }


    return (
        <>
            <AuthCuadroMandoContext.Provider
                value={{
                    isAuthenticated,
                    loading,
                    logOut,
                    AvailableRoutes,
                    getUserCMIProfile
                }}
            >
                {children}
            </AuthCuadroMandoContext.Provider>

            <CustomModal
                dataModal={dataModal}
                setDataModal={async (value) => {
                    if (value.success) {
                        REMOVE_TOKEN_SYSTEM();
                        clearPersistedState();
                        await LogoutMSSO(instance, accounts); 
                        setIsAuthenticated(false);
                        alerts.success("Sesion cerrada correctamente");
                    }
                    setDataModal({ ...dataModal, modalVisible: value.modalVisible })
                }
                }
            /> 
        </>
    )

}
export const useAuthCuadroMando = () => useContext(AuthCuadroMandoContext);

